// Edit an automation opportunity in place. Owner-only edit surface — see
// migration 0051 (automations table). Every save appends {edited_at, field,
// before, after, edited_by} into automations.edit_history JSONB, and updates
// the columns via a single UPDATE that RLS allows because is_owner(owner_id).
//
// The dedupe key (opportunity_key) is NEVER mutated — it's the stable identity
// used by the Brain's ON CONFLICT DO NOTHING upsert.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import type { AutomationRow } from '@/pages/Intel';

type Draft = {
  opportunity: string;
  evidence: string;
  agent_type: 'agent' | 'skill';
  data_points: string; // strings for input; parsed to int on save
  data_points_target: string;
  est_time_saved_per_week_minutes: string;
};

function toDraft(r: AutomationRow): Draft {
  return {
    opportunity: r.opportunity,
    evidence: r.evidence ?? '',
    agent_type: r.agent_type ?? 'agent',
    data_points: r.data_points != null ? String(r.data_points) : '',
    data_points_target: r.data_points_target != null ? String(r.data_points_target) : '',
    est_time_saved_per_week_minutes:
      r.est_time_saved_per_week_minutes != null ? String(r.est_time_saved_per_week_minutes) : '',
  };
}

// Compute the diff between the saved row and the drafted form. One
// history entry per field the owner actually touched. Numeric parses
// coerce "" → null so a cleared field records as "was N → null".
type Change = { field: string; before: unknown; after: unknown };

function computeChanges(row: AutomationRow, draft: Draft): Change[] {
  const parseNum = (s: string): number | null => {
    const t = s.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  };
  const c: Change[] = [];
  if (draft.opportunity.trim() !== row.opportunity) {
    c.push({ field: 'opportunity', before: row.opportunity, after: draft.opportunity.trim() });
  }
  const evNew = draft.evidence.trim() || null;
  if (evNew !== (row.evidence ?? null)) {
    c.push({ field: 'evidence', before: row.evidence, after: evNew });
  }
  if (draft.agent_type !== (row.agent_type ?? 'agent')) {
    c.push({ field: 'agent_type', before: row.agent_type, after: draft.agent_type });
  }
  const dp = parseNum(draft.data_points);
  if (dp !== (row.data_points ?? null)) {
    c.push({ field: 'data_points', before: row.data_points, after: dp });
  }
  const target = parseNum(draft.data_points_target);
  if (target !== (row.data_points_target ?? null)) {
    c.push({ field: 'data_points_target', before: row.data_points_target, after: target });
  }
  const eta = parseNum(draft.est_time_saved_per_week_minutes);
  if (eta !== (row.est_time_saved_per_week_minutes ?? null)) {
    c.push({
      field: 'est_time_saved_per_week_minutes',
      before: row.est_time_saved_per_week_minutes,
      after: eta,
    });
  }
  return c;
}

export default function EditAutomationModal({
  row,
  open,
  onOpenChange,
}: {
  row: AutomationRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(row));

  // Re-sync when a different row is opened (or the row updates from elsewhere)
  // and the modal re-opens.
  useEffect(() => {
    if (open) setDraft(toDraft(row));
  }, [open, row]);

  const save = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No user');
      const changes = computeChanges(row, draft);
      if (changes.length === 0) return { changes: 0 };
      const parseNum = (s: string): number | null => {
        const t = s.trim();
        if (!t) return null;
        const n = Number(t);
        return Number.isFinite(n) ? Math.trunc(n) : null;
      };
      const nowIso = new Date().toISOString();
      const historyAdds = changes.map((c) => ({
        edited_at: nowIso,
        field: c.field,
        before: c.before,
        after: c.after,
        edited_by: user.id,
      }));
      const { error } = await supabase
        .from('automations')
        .update({
          opportunity: draft.opportunity.trim(),
          evidence: draft.evidence.trim() || null,
          agent_type: draft.agent_type,
          data_points: parseNum(draft.data_points),
          data_points_target: parseNum(draft.data_points_target),
          est_time_saved_per_week_minutes: parseNum(draft.est_time_saved_per_week_minutes),
          edit_history: [...(row.edit_history ?? []), ...historyAdds],
        })
        .eq('id', row.id);
      if (error) throw error;
      return { changes: changes.length };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['automations'] });
      if (res.changes === 0) toast('No changes to save.');
      else toast.success(`Saved ${res.changes} change${res.changes === 1 ? '' : 's'}.`);
      onOpenChange(false);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not save.'),
  });

  // Guard: opportunity required, target ≥1, data_points ≥0, est_time_saved ≥0
  // — matches migration 0051 CHECK constraints. Validate before submit so the
  // DB never rejects the write (HTML min={} only blocks spinner clicks; typed
  // input flows through unchecked).
  const opportunityTrim = draft.opportunity.trim();
  const targetStr = draft.data_points_target.trim();
  const targetNum = targetStr ? Number(targetStr) : null;
  const targetValid = targetNum === null || (Number.isFinite(targetNum) && targetNum >= 1);
  const dpStr = draft.data_points.trim();
  const dpNum = dpStr ? Number(dpStr) : null;
  const dpValid = dpNum === null || (Number.isFinite(dpNum) && dpNum >= 0);
  const etaStr = draft.est_time_saved_per_week_minutes.trim();
  const etaNum = etaStr ? Number(etaStr) : null;
  const etaValid = etaNum === null || (Number.isFinite(etaNum) && etaNum >= 0);
  const canSave = opportunityTrim.length > 0 && targetValid && dpValid && etaValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit automation</DialogTitle>
          <DialogDescription>
            Refine what the Brain proposed. Your edits stick across future digests.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="opp" className="text-xs">
              Opportunity
            </Label>
            <Input
              id="opp"
              value={draft.opportunity}
              onChange={(e) => setDraft((d) => ({ ...d, opportunity: e.target.value }))}
              className="mt-1"
              aria-invalid={!opportunityTrim}
            />
          </div>

          <div>
            <Label htmlFor="ev" className="text-xs">
              Evidence
            </Label>
            <Textarea
              id="ev"
              value={draft.evidence}
              onChange={(e) => setDraft((d) => ({ ...d, evidence: e.target.value }))}
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Type</Label>
            <div className="mt-1 flex gap-2">
              {(['agent', 'skill'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setDraft((d) => ({ ...d, agent_type: t }))}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    draft.agent_type === t
                      ? 'bg-forest text-primary-foreground border-forest'
                      : 'bg-secondary text-muted-foreground border-border hover:text-foreground'
                  }`}
                >
                  {t === 'agent' ? 'New Agent' : 'New Skill'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="dp" className="text-xs">
                Data points
              </Label>
              <Input
                id="dp"
                type="number"
                min={0}
                value={draft.data_points}
                onChange={(e) => setDraft((d) => ({ ...d, data_points: e.target.value }))}
                className="mt-1"
                aria-invalid={!dpValid}
              />
            </div>
            <div>
              <Label htmlFor="dpt" className="text-xs">
                Target (≥1)
              </Label>
              <Input
                id="dpt"
                type="number"
                min={1}
                value={draft.data_points_target}
                onChange={(e) => setDraft((d) => ({ ...d, data_points_target: e.target.value }))}
                className="mt-1"
                aria-invalid={targetNum !== null && (!Number.isFinite(targetNum) || targetNum < 1)}
              />
            </div>
            <div>
              <Label htmlFor="eta" className="text-xs">
                min/wk saved
              </Label>
              <Input
                id="eta"
                type="number"
                min={0}
                value={draft.est_time_saved_per_week_minutes}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, est_time_saved_per_week_minutes: e.target.value }))
                }
                className="mt-1"
                aria-invalid={!etaValid}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {save.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Export computeChanges for unit tests — the diff logic is what makes the
// audit trail meaningful and needs regression coverage.
export { computeChanges as __computeChanges };
